import React, { useState } from 'react';
import { makeStyles, Box, Typography, Paper, Button, CircularProgress, Tooltip, IconButton } from '@material-ui/core';
import PersonIcon from '@material-ui/icons/Person';
import { formatRelativeTime } from '../../../utils/commonUtils';
import GetAppIcon from '@material-ui/icons/GetApp';

const useStyles = makeStyles((theme) => ({
  msgWrapper: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(4),
    width: '100%',
  },
  msgWrapperHuman: {
    flexDirection: 'row-reverse',
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    flexShrink: 0,
  },
  bubble: ({ noPadding }) => ({
    padding: noPadding ? 0 : theme.spacing(2, 3),
    borderRadius: 16,
    position: 'relative',
    maxWidth: '100%',
    overflow: 'hidden'
  }),
  aiBubble: {
    backgroundColor: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: '16px 16px 16px 4px',
    color: '#334155',
  },
  humanBubble: {
    backgroundColor: '#34445c', // Your brand's deep slate
    color: '#fff',
    borderRadius: '16px 16px 4px 16px',
  },
  aiErrorBubble: {
    border: '1px solid #fecaca',
    backgroundColor: '#fef2f2'
  },
  timeText: {
    fontSize: '0.65rem',
    marginTop: theme.spacing(1),
    display: 'block',
    opacity: 0.7,
  },
  stepTitle: {
    fontWeight: 800,
    fontSize: '0.7rem',
    // Using your brand yellow for the "active" indicator text
    color: '#EAB308',
    textTransform: 'none',
    display: 'block'
  },
  listItem: {
    borderBottom: "1px solid #eee",
    position: "relative",
    "&:last-child": {
      borderBottom: "none"
    }
  },
  listTitle: {
    fontWeight: 600,
    fontSize: "0.85rem",
    wordBreak: 'break-word',
    overflowWrap: 'break-word'
  },
  listDescription: {
    fontSize: "0.8rem",
    color: "#555"
  },
  objectItem: {
    position: "relative",
    borderBottom: "1px solid #eee",
    "&:last-child": {
      borderBottom: "none"
    }
  },
  objectlistItem: {
    marginBottom: '5px'
  },
  objectTitle: {
    fontWeight: 700,
    fontSize: "0.9rem",
    marginBottom: '0.5rem'
  },
  actionArea: {
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-end',
  },
  proceedBtn: {
    backgroundColor: '#ffea00', // Brand Yellow
    color: '#34445c',
    fontWeight: 700,
    fontSize: '0.7rem',
    textTransform: 'none',
    borderRadius: 6,
    boxShadow: 'none',
    padding: '4px 12px',
    marginLeft: theme.spacing(1), // Spacing instead of gap
    '&:hover': {
      backgroundColor: '#e5d100', // Slightly darker yellow
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
  },
  revertBtn: {
    borderColor: '#e2e8f0',
    color: '#64748b',
    fontSize: '0.7rem',
    textTransform: 'none',
    borderRadius: 6,
    padding: '4px 12px',
    '&:hover': {
      borderColor: '#cbd5e1',
      backgroundColor: 'rgba(0,0,0,0.02)',
      color: '#334155',
    },
  },
  contentRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  downloadIconBtn: {
    padding: 4,
    color: '#64748b',
    '&:hover': {
      color: '#34445c',
      backgroundColor: 'rgba(52,68,92,0.06)',
    },
  },
  downloadIconWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 18,
    height: 18,
  },
}));

const MessageBubble = ({ message = {}, children, noPadding }) => {
  const classes = useStyles({ noPadding });

  const { status, role, type, content, time, stepTitle, proceed, handleProceed, revert, handleRevert, download, downloadProgress, handleDownload } = message;
  const [completed, setCompleted] = useState(false)
  const isAI = role === 'ai';


  return (
    <Box className={`${classes.msgWrapper} ${!isAI ? classes.msgWrapperHuman : ''}`}>
      {/* Avatar Toggle */}
      <Box
        className={classes.avatar}
        style={{ backgroundColor: isAI ? '#34445c' : '#475569' }}
      >
        {isAI ? <img src="/node_icons/ai.png" style={{ width: '60%' }} alt="hyperblox agent" /> : <PersonIcon fontSize="small" />}
      </Box>

      {/* Bubble Content */}
      <Box display="flex" flexDirection="column" alignItems={isAI ? 'flex-start' : 'flex-end'} style={{ minWidth: 0, maxWidth: '100%' }}>
        <Paper
          elevation={0}
          className={`${classes.bubble} ${(isAI && status) ? classes.aiBubble : (isAI && !status) ? classes.aiErrorBubble : classes.humanBubble}`}
        >
          {Array.isArray(content) ? (
            <Box>
              {stepTitle && (
                <Typography variant="overline" className={classes.stepTitle}>
                  {stepTitle}
                </Typography>
              )}
              {content.map((item, index) => (
                <Box key={index} mb={1.5} className={classes.listItem}>
                  <Typography variant="body2" className={classes.listTitle}>
                    {item.name}
                  </Typography>
                  <Typography variant="body2" className={classes.listDescription}>
                    {item.description}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : typeof content === "object" && content !== null ? (
            <Box>
              {stepTitle && (
                <Typography variant="overline" className={classes.stepTitle}>
                  {stepTitle}
                </Typography>
              )}
              {Object.entries(content).map(([key, value], index) => (
                <Box key={index} mb={1} className={classes.objectItem}>
                  <Typography variant="body1" className={classes.objectTitle}>
                    {key}:
                  </Typography>
                  <Box ml={2}>
                    {(value || []).map((item, index) => (
                      <Box key={index} mb={1.5} className={classes.objectlistItem}>
                        <Typography variant="body2" className={classes.listTitle}>
                          {item.name}
                        </Typography>
                        <Typography variant="body2" className={classes.listDescription}>
                          {item.description}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <>
              {stepTitle && (
                <Typography variant="overline" className={classes.stepTitle}>
                  {stepTitle}
                </Typography>
              )}
              {
                content &&
                <Box className={classes.contentRow}>
                  <Typography variant="body2" style={{ lineHeight: 1.6 }}>
                    {content}
                  </Typography>
                  {download && (
                    <Tooltip title={downloadProgress != null ? `Downloading…` : 'Download sample data'}>
                      <span>
                        <IconButton
                          size="small"
                          className={classes.downloadIconBtn}
                          disabled={downloadProgress != null}
                          onClick={handleDownload}
                        >
                          {downloadProgress != null ? (
                            <Box className={classes.downloadIconWrap}>
                              <CircularProgress
                                size={16}
                                thickness={5}
                                variant={typeof downloadProgress === 'number' ? 'determinate' : 'indeterminate'}
                                value={typeof downloadProgress === 'number' ? downloadProgress : undefined}
                                style={{ color: '#34445c' }}
                              />
                            </Box>
                          ) : (
                            <GetAppIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              }
              {/* This allows you to pass custom things like the "heat-map" placeholder */}
              {children}
              {/* Action Buttons */}
              {
                (revert || proceed) &&
                <Box className={classes.actionArea}>
                  {!completed && (
                    /* Active State */
                    <>
                      {revert && (
                        <Button
                          variant="outlined"
                          size="small"
                          className={classes.revertBtn}
                          onClick={() => {
                            setCompleted(true);
                            handleRevert();
                          }}
                        >
                          {revert}
                        </Button>
                      )}

                      {proceed && (
                        <Button
                          variant="contained"
                          size="small"
                          className={classes.proceedBtn}
                          onClick={() => {
                            setCompleted(true);
                            handleProceed();
                          }}
                        >
                          {proceed}
                        </Button>
                      )}
                    </>
                  )}
                </Box>
              }
            </>
          )}

        </Paper>

        <Typography className={classes.timeText} style={{ color: isAI ? '#64748b' : '#94a3b8' }}>
          {formatRelativeTime(time)}
        </Typography>
      </Box>
    </Box>
  );
};

export default MessageBubble;